import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getSharedMicStream,
  type ConnectionStatus,
  type UseVoiceSessionReturn,
} from '@orborix/voice-client';
import { POST_ROLL_MS } from './postRoll.js';

/**
 * Máquina del turno push-to-talk (captureMode `'hold'`): el mic queda CERRADO
 * entre turnos y sólo captura mientras el usuario mantiene apretado. Al soltar
 * se commitea el turno (STT → LLM); deslizar a la izquierda lo descarta sin
 * dejar huella; deslizar hacia ARRIBA lo FIJA (como WhatsApp): la grabación
 * sigue sin sostener el botón y se cierra con enviar/descartar explícitos.
 *
 * Versión hold-only del `useRecordingTurn` de speak. El package NO gatea la
 * captura por captureMode — sin el `pauseCapture()` entre turnos el mic
 * seguiría escuchando aunque el server no auto-commitee.
 *
 * PREVIEW LOCAL (paridad WhatsApp): en paralelo al pipeline, cada turno se
 * graba localmente con un MediaRecorder sobre el MISMO stream compartido
 * (`getSharedMicStream` — sin segundo getUserMedia). Al PAUSAR, el take
 * acumulado queda disponible como `takeUrl` para que la UI ofrezca
 * escucharse antes de seguir/enviar. El buffer vive sólo durante el turno
 * (send/cancel/start lo descartan y revocan la URL). Si el entorno no tiene
 * MediaRecorder o el stream todavía no existe, el preview simplemente no
 * aparece (capacidad opcional, no config).
 *
 * CAÍDA DEL WS A MITAD DEL TURNO: los frames PCM viajan por el socket, pero el
 * contador y la waveform los alimenta el mic LOCAL — con el socket caído todo
 * SEGUÍA moviéndose mientras el audio no llegaba a ningún lado, y al soltar el
 * turno se perdía MUDO. Ahora perder la conexión corta el turno: lo fija, lo
 * pausa y conserva el take local. Ver {@link HoldRecorder.interrupted}.
 *
 * POST-ROLL AL SOLTAR: lo último que dijo el user todavía no llegó al worklet
 * cuando suelta (latencia de entrada: 100-300 ms con Bluetooth). `send()` no
 * cierra la captura en el acto: el commit sigue mandando `POST_ROLL_MS` y
 * recién ahí sale el `turn-stop` y se pausa el mic (`onSettled`). La UI no lo
 * ve: el recorder ya volvió a reposo y `sending` se prende al soltar. Un
 * re-apretón durante el post-roll se IGNORA (`postRolling`): ese
 * turno está en vuelo, como en 'thinking' — abrir otro mezclaría su audio con
 * la cola del anterior o le cortaría la cola. Descartar (`cancel()`) nunca
 * pasa por el post-roll: el `turn-discard` sale en el acto.
 */
export interface HoldRecorder {
  /** Capturando audio (hold en curso o grabación fijada). */
  recording: boolean;
  /** Grabación fijada (deslizó hacia arriba o tap rápido): soltar NO envía;
   *  el cierre es por los botones enviar/descartar. */
  locked: boolean;
  /** Grabación fijada EN PAUSA (botón pausa): captura y contador congelados
   *  hasta `resume()`. Enviar/descartar siguen disponibles. */
  paused: boolean;
  /**
   * El turno se cortó porque la conexión se cayó: quedó FIJADO y EN PAUSA con
   * el take local intacto (`takeUrl`), pero ese audio NUNCA llegó al server.
   * No hay reenvío posible — el transporte es PCM en vivo por el WS y la
   * reconexión reconstruye el pipeline server-side (el buffer del STT no
   * sobrevive), así que un "reenviar" sería un botón que no reenvía nada. Las
   * únicas salidas son escuchar el take y `cancel()` para regrabar; `send()` y
   * `resume()` quedan cerrados mientras dure. Se limpia con `cancel()`/`start()`.
   */
  interrupted: boolean;
  /**
   * Commit del turno en vuelo (tap de enviar → transcript confirmado por el
   * server; 300-1000ms medidos, 2026-07-26). Espejo de
   * `session.commitPending` para que la UI muestre "enviando…" sin cablear
   * la señal aparte — sin feedback el tap se siente frizado y el usuario
   * re-toca, arrancando un turno nuevo que ensucia el commit del anterior.
   */
  sending: boolean;
  /**
   * El turno soltado todavía manda su cola (post-roll) y su `turn-stop` no
   * salió. Mientras dure, `start()` no abre otro turno y el gesto no se arma.
   * Opcional para que los recorders de maqueta de las apps sigan valiendo
   * (ausente = false).
   */
  postRolling?: boolean;
  /**
   * El último envío murió sin transcript (audio perdido / commit ignorado —
   * la señal es el incremento de `session.turnEmptyCount`). Se apaga solo a
   * los 5s. El composer lo muestra como aviso (`labels.nothingHeard`); queda
   * en el contrato para UIs que quieran reaccionar además del aviso.
   */
  sendLost: boolean;
  /**
   * El mic no entrega SONIDO: grabando, ~3 s sin una sola muestra audible
   * (`NO_SOUND_AFTER_MS`); se apaga apenas entra sonido. Y al soltar un turno
   * que no tuvo ni un rastro de sonido, queda 5 s en lugar del `sendLost`.
   *
   * Por qué: en Android 10+, si otra app tiene el mic (una llamada, el modo voz
   * de ChatGPT), Chrome recibe SILENCIO, no un error — el pre-flight pasa, la
   * charla arranca por voz y la waveform queda plana sin explicación. Medir el
   * nivel es la única señal. El umbral va contra el PICO del turno, no el
   * promedio: un mic sano callado (con la supresión de ruido del aparato)
   * también da casi cero; lo que lo separa de uno mudo es que al hablar el pico
   * sube. El composer lo muestra como `labels.noSound`.
   */
  noSound: boolean;
  /** Object URL del take grabado hasta la pausa (preview "escucharme"), o
   *  null si no está pausado / el entorno no soporta el preview. */
  takeUrl: string | null;
  /**
   * Historial de amplitud del turno (una muestra cada ~100ms, envelope del
   * RMS real [0..1]). Alimenta la waveform deslizante mientras se graba y la
   * vista completa (zoom out) de la pausa. OJO: devuelve la VISTA VIVA del
   * buffer (misma instancia, muta por push) — leer desde RAF; NO apta para
   * memoización por referencia (copiá antes si necesitás identidad estable).
   */
  getTakeSamples: () => readonly number[];
  /** Segundos de grabación del turno en curso. */
  elapsed: number;
  /** RMS instantáneo del mic [0..1] para la waveform (leer desde RAF). */
  getEnergy: () => number;
  /**
   * Pointer down: abre el mic (interrumpe a la voz viva si estaba hablando).
   * No-op durante el post-roll del turno anterior (ver {@link postRolling}).
   */
  start: () => void;
  /** Fija la grabación en curso (gesto hacia arriba). No-op sin grabación. */
  lock: () => void;
  /** Pausa la grabación fijada (mic + contador). No-op sin grabación. */
  pause: () => void;
  /** Retoma una grabación pausada. No-op si no está pausada. */
  resume: () => void;
  /** Cierra el mic y despacha el turno (soltar, o tap en enviar si fijada). */
  send: () => void;
  /** Descarta el audio sin commitear (soltar armado, o tap en el tacho). */
  cancel: () => void;
}

export interface HoldTurnOpts {
  /**
   * Modo charla activo (`useFreeChat`): el recorder queda en reposo y NO debe
   * pausar la captura en las transiciones del pipeline — el mic abierto es
   * exactamente el punto del modo.
   */
  suspended?: boolean;
  /**
   * Post-roll al soltar (ms). Default {@link POST_ROLL_MS}; las apps no lo
   * tocan — existe para el banco E2E (`0` = sin post-roll, el control).
   */
  postRollMs?: number;
}

/** Cadencia del historial de amplitud (una barra de la waveform por muestra). */
export const SAMPLE_MS = 100;

/** Duración del aviso "no se escuchó" antes de apagarse solo. */
const SEND_LOST_NOTICE_MS = 5_000;

/**
 * RMS mínimo de una muestra para contar como "llegó sonido". Un mic que otra
 * app tiene tomado entrega ceros exactos; un Android sano callado midió un
 * piso de 6,4e-7 y la voz queda órdenes de magnitud arriba — es el mismo
 * `MIN_AUDIBLE_RMS` del banco de eco.
 */
export const MIN_AUDIBLE_RMS = 0.001;

/** Grabando sin una muestra audible durante este tiempo → aviso "no llega sonido". */
export const NO_SOUND_AFTER_MS = 3_000;

/**
 * Al soltar, el aviso "no llega sonido" reemplaza al genérico sólo si el turno
 * duró al menos esto: el RMS del worklet llega cada ~50 ms, y un toque cortito
 * puede soltarse antes de la primera medición (no sería un mic mudo).
 */
const NO_SOUND_MIN_TURN_MS = 1_000;

/**
 * Estados de conexión en los que el audio del turno YA NO llega al server.
 * `connecting` e `idle` quedan afuera a propósito: son el arranque y el cierre
 * DELIBERADO de la sesión (los gobierna la app, con su propio copy), no una
 * caída.
 */
function isConnectionBroken(status: ConnectionStatus): boolean {
  return status === 'reconnecting' || status === 'lost';
}

export function useHoldTurn(session: UseVoiceSessionReturn, opts: HoldTurnOpts = {}): HoldRecorder {
  // `session` cambia de referencia por render; ref estable para los handlers.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const suspendedRef = useRef(opts.suspended === true);
  suspendedRef.current = opts.suspended === true;
  const postRollMs = opts.postRollMs ?? POST_ROLL_MS;
  const postRollMsRef = useRef(postRollMs);
  postRollMsRef.current = postRollMs;
  // El turno soltado sigue mandando su cola (post-roll) y todavía no salió su
  // `turn-stop`. Ref sincrónico: lo leen `start()` y el guard de captura.
  const [postRolling, setPostRolling] = useState(false);
  const postRollingRef = useRef(false);

  // Los refs espejo se actualizan SINCRÓNICOS en cada transición (no vía
  // render): en un tap muy rápido, lock()/pause() corren en el mismo frame
  // que start() y el estado de React todavía no re-renderizó — sin el ref al
  // día, el tap-para-fijar se perdía.
  const [recording, setRecording] = useState(false);
  const recordingRef = useRef(false);
  const [locked, setLocked] = useState(false);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const [interrupted, setInterrupted] = useState(false);
  const interruptedRef = useRef(false);
  // Aviso "no se escuchó": observa el contador MONOTÓNICO de turnos vacíos y
  // se apaga solo (mismo patrón que el muteNotice de speak). Se compara con
  // un prevRef (no `!== 0`): un remount del recorder no debe re-mostrar
  // avisos de envíos viejos.
  const [sendLost, setSendLost] = useState(false);
  const sendLostTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevTurnEmptyRef = useRef(session.turnEmptyCount);
  useEffect(() => {
    if (session.turnEmptyCount <= prevTurnEmptyRef.current) return;
    prevTurnEmptyRef.current = session.turnEmptyCount;
    if (swallowNextEmptyRef.current) {
      swallowNextEmptyRef.current = false;
      return;
    }
    setSendLost(true);
    if (sendLostTimerRef.current != null) clearTimeout(sendLostTimerRef.current);
    sendLostTimerRef.current = setTimeout(() => setSendLost(false), SEND_LOST_NOTICE_MS);
  }, [session.turnEmptyCount]);
  useEffect(
    () => () => {
      if (sendLostTimerRef.current != null) clearTimeout(sendLostTimerRef.current);
    },
    [],
  );
  // Aviso "no llega sonido". `heardRef` = el turno en curso ya tuvo una
  // muestra audible; `silentMsRef` = ms grabados (sin pausa) del turno. Se
  // muestrean en el mismo tick que la waveform (sin otro timer).
  const [noSound, setNoSound] = useState(false);
  const noSoundRef = useRef(false);
  const heardRef = useRef(false);
  const silentMsRef = useRef(0);
  const noSoundTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Un envío sin sonido ya avisó "no llega sonido": el `turnEmptyCount` que el
  // server devuelve por ese mismo turno no debe pisarlo con "no se escuchó".
  const swallowNextEmptyRef = useRef(false);
  const showNoSound = useCallback((on: boolean): void => {
    if (noSoundTimerRef.current != null) {
      clearTimeout(noSoundTimerRef.current);
      noSoundTimerRef.current = null;
    }
    if (noSoundRef.current === on) return;
    noSoundRef.current = on;
    setNoSound(on);
  }, []);
  useEffect(
    () => () => {
      if (noSoundTimerRef.current != null) clearTimeout(noSoundTimerRef.current);
    },
    [],
  );
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [takeUrl, setTakeUrl] = useState<string | null>(null);
  const takeRef = useRef<{ mr: MediaRecorder; chunks: Blob[] } | null>(null);
  const takeUrlRef = useRef<string | null>(null);

  const samplesRef = useRef<number[]>([]);
  const sampleTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sampleEnvRef = useRef(0);

  const clearTimer = useCallback((): void => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopSampling = useCallback((): void => {
    if (sampleTimerRef.current) {
      clearInterval(sampleTimerRef.current);
      sampleTimerRef.current = null;
    }
  }, []);

  /**
   * Muestreo del historial de amplitud (cada `SAMPLE_MS`): el RMS del worklet
   * refresca cada ~250ms, así que entre updates el envelope decae suavemente
   * hacia el valor crudo (ataque instantáneo, release mezclado) — barras
   * derivadas del audio real, sin plateaus duros.
   */
  const startSampling = useCallback((): void => {
    stopSampling();
    sampleTimerRef.current = setInterval(() => {
      // Guard NaN: un solo valor no-finito envenenaría el envelope para el
      // resto del turno (NaN > x es false y el blend propaga NaN).
      const energy = sessionRef.current.getMicEnergy();
      const raw = Number.isFinite(energy) ? Math.max(0, Math.min(1, energy)) : 0;
      sampleEnvRef.current =
        raw > sampleEnvRef.current ? raw : sampleEnvRef.current * 0.7 + raw * 0.3;
      samplesRef.current.push(sampleEnvRef.current);
      silentMsRef.current += SAMPLE_MS;
      if (raw >= MIN_AUDIBLE_RMS) {
        heardRef.current = true;
        if (noSoundRef.current) showNoSound(false);
      } else if (!heardRef.current && silentMsRef.current >= NO_SOUND_AFTER_MS) {
        if (!noSoundRef.current) showNoSound(true);
      }
    }, SAMPLE_MS);
  }, [stopSampling, showNoSound]);

  const revokeTakeUrl = useCallback((): void => {
    if (takeUrlRef.current !== null) {
      URL.revokeObjectURL(takeUrlRef.current);
      takeUrlRef.current = null;
    }
    setTakeUrl(null);
  }, []);

  /** Cierra y descarta el take local del turno (buffer + URL). */
  const discardTake = useCallback((): void => {
    const take = takeRef.current;
    takeRef.current = null;
    if (take && take.mr.state !== 'inactive') {
      try {
        take.mr.stop();
      } catch {
        /* ya detenido */
      }
    }
    revokeTakeUrl();
  }, [revokeTakeUrl]);

  /** Arranca el take local del turno sobre el stream compartido del mic. */
  const beginTake = useCallback((): void => {
    if (typeof MediaRecorder === 'undefined') return; // capacidad opcional
    const stream = getSharedMicStream();
    if (stream === null) return; // el pipeline todavía no adquirió el mic
    try {
      const mr = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      mr.ondataavailable = (e: BlobEvent): void => {
        if (e.data.size > 0) chunks.push(e.data);
        // El flush del pause() (requestData + pause síncronos) llega acá con
        // el estado ya en 'paused': armamos el preview con TODO lo acumulado.
        // También cubre 'inactive': si la conexión muere del todo, el teardown
        // del pipeline suelta el MediaStream y el recorder se cierra solo — ese
        // último dataavailable trae el take entero y queremos conservarlo igual.
        // (Un take ya descartado no pasa: `discardTake` nulea `takeRef` ANTES
        // de parar el recorder.)
        if (mr.state !== 'recording' && takeRef.current?.mr === mr && chunks.length > 0) {
          revokeTakeUrl();
          const url = URL.createObjectURL(new Blob(chunks, { type: mr.mimeType }));
          takeUrlRef.current = url;
          setTakeUrl(url);
        }
      };
      mr.start();
      takeRef.current = { mr, chunks };
    } catch (err) {
      console.warn('[voice-composer] preview local no disponible (MediaRecorder falló)', err);
    }
  }, [revokeTakeUrl]);

  useEffect(
    () => () => {
      clearTimer();
      stopSampling();
      discardTake();
    },
    [clearTimer, stopSampling, discardTake],
  );

  // Mic cerrado entre turnos: en cada transición de estado del pipeline, si no
  // hay un hold en curso, la captura queda pausada (cubre el arranque de la
  // sesión y cualquier transición server-side que la reabra). Con el modo
  // charla activo (`suspended`) el guard NO corre: ahí el mic vive abierto.
  const { state } = session;
  useEffect(() => {
    if (state === 'idle') return;
    if (suspendedRef.current) return;
    // Durante el post-roll la captura sigue abierta a propósito: la pausa la
    // hace el propio commit al terminar.
    if (!recordingRef.current && !postRollingRef.current) sessionRef.current.pauseCapture();
  }, [state]);

  const start = useCallback((): void => {
    // El turno anterior todavía manda su cola: un turno nuevo ahora mezclaría
    // audio de los dos (ver el header § POST-ROLL).
    if (postRollingRef.current) return;
    const s = sessionRef.current;
    // Barge-in: apretar mientras la voz viva habla (o su audio sigue drenando
    // en el player) la interrumpe — sin esto hablás al vacío half-duplex.
    if (s.state === 'speaking' || s.isAudioPlaying) void s.interrupt();
    if (s.paused) s.resumeSession();
    s.resumeCapture();
    setRecording(true);
    recordingRef.current = true;
    setLocked(false);
    setPaused(false);
    pausedRef.current = false;
    setInterrupted(false);
    interruptedRef.current = false;
    setElapsed(0);
    clearTimer();
    timerRef.current = setInterval(() => setElapsed((e) => e + 0.1), 100);
    discardTake();
    beginTake();
    samplesRef.current = [];
    sampleEnvRef.current = 0;
    heardRef.current = false;
    silentMsRef.current = 0;
    swallowNextEmptyRef.current = false;
    showNoSound(false);
    startSampling();
  }, [clearTimer, discardTake, beginTake, startSampling, showNoSound]);

  const lock = useCallback((): void => {
    if (recordingRef.current) setLocked(true);
  }, []);

  const pause = useCallback((): void => {
    if (!recordingRef.current || pausedRef.current) return;
    clearTimer();
    stopSampling();
    sessionRef.current.pauseCapture();
    setPaused(true);
    pausedRef.current = true;
    // Flush del take local ANTES de pausar el MediaRecorder: requestData()
    // encola el dataavailable con lo bufferizado y pause() cambia el estado
    // en sincrónico — cuando el evento llega, el handler ya ve 'paused' y
    // arma la URL del preview.
    const take = takeRef.current;
    if (take && take.mr.state === 'recording') {
      try {
        take.mr.requestData();
        take.mr.pause();
      } catch (err) {
        console.warn('[voice-composer] flush del preview local falló', err);
      }
    }
  }, [clearTimer, stopSampling]);

  /**
   * Corta el turno en curso porque la conexión se cayó: lo FIJA (el hold ya no
   * sostiene nada — el gesto puede seguir apretado, pero el panel toma el
   * lugar del mic) y lo PAUSA, que es lo que congela el contador y la
   * waveform, cierra la captura y flushea el take local al preview.
   */
  const interruptTurn = useCallback((): void => {
    setInterrupted(true);
    interruptedRef.current = true;
    setLocked(true);
    pause();
  }, [pause]);

  // Caída del WS con un turno en vuelo: el mic local seguía moviendo contador y
  // waveform mientras los frames no llegaban a ningún lado. `recording` también
  // es dependencia porque un hold que ARRANCA con la conexión ya caída (speak y
  // fit no bloquean el mic mientras reconecta) tiene que nacer interrumpido —
  // decirlo de entrada es mejor que dejarlo hablar diez segundos al vacío.
  const { connectionStatus } = session;
  useEffect(() => {
    if (!isConnectionBroken(connectionStatus)) return;
    if (!recordingRef.current || interruptedRef.current) return;
    interruptTurn();
  }, [connectionStatus, recording, interruptTurn]);

  const resume = useCallback((): void => {
    if (!recordingRef.current || !pausedRef.current) return;
    // Un turno interrumpido no se retoma: lo grabado hasta la caída no llegó al
    // server, así que seguir grabando dejaría un take que dice una cosa y un
    // turno commiteado que dice otra.
    if (interruptedRef.current) return;
    sessionRef.current.resumeCapture();
    setPaused(false);
    pausedRef.current = false;
    clearTimer();
    timerRef.current = setInterval(() => setElapsed((e) => e + 0.1), 100);
    startSampling();
    // El preview deja de valer (la grabación sigue); el buffer se conserva —
    // la próxima pausa arma el take COMPLETO desde el arranque del turno.
    revokeTakeUrl();
    const take = takeRef.current;
    if (take && take.mr.state === 'paused') {
      try {
        take.mr.resume();
      } catch (err) {
        console.warn('[voice-composer] resume del preview local falló', err);
      }
    }
  }, [clearTimer, revokeTakeUrl, startSampling]);

  const send = useCallback((): void => {
    // Turno interrumpido: NO se despacha. El audio nunca llegó al server, así
    // que commitear cerraría un turno vacío (y de paso tiraría el take local,
    // lo único que quedó del turno). La UI no ofrece enviar en ese estado; el
    // guard cubre el gesto que quedó a medio soltar cuando el panel se montó
    // solo.
    if (interruptedRef.current) return;
    // Sin turno abierto no hay nada que mandar (p. ej. el soltar de un
    // re-apretón que `start()` ignoró durante el post-roll).
    if (!recordingRef.current) return;
    clearTimer();
    setRecording(false);
    recordingRef.current = false;
    setLocked(false);
    setPaused(false);
    pausedRef.current = false;
    setElapsed(0);
    // Turno sin un solo rastro de sonido: el aviso que sirve es "no llega
    // sonido", no el genérico "no se escuchó" que el server va a disparar por
    // ese mismo envío vacío (y que pide repetir justo lo que no va a andar).
    const silentTurn = !heardRef.current && silentMsRef.current >= NO_SOUND_MIN_TURN_MS;
    if (silentTurn) {
      swallowNextEmptyRef.current = true;
      showNoSound(true);
      noSoundTimerRef.current = setTimeout(() => showNoSound(false), SEND_LOST_NOTICE_MS);
    } else {
      showNoSound(false);
    }
    const s = sessionRef.current;
    // Post-roll: la captura sigue abierta hasta que salga el `turn-stop`; el
    // "enviando…" (`commitPending`) ya se prendió adentro de `commitTurn`.
    postRollingRef.current = true;
    setPostRolling(true);
    s.commitTurn({
      postRollMs: postRollMsRef.current,
      onSettled: () => {
        postRollingRef.current = false;
        setPostRolling(false);
        // Modo charla prendido mientras tanto: ahí el mic vive abierto.
        if (!recordingRef.current && !suspendedRef.current) sessionRef.current.pauseCapture();
      },
    });
    stopSampling();
    samplesRef.current = [];
    discardTake();
  }, [clearTimer, stopSampling, discardTake, showNoSound]);

  const cancel = useCallback((): void => {
    clearTimer();
    setRecording(false);
    recordingRef.current = false;
    setLocked(false);
    setPaused(false);
    pausedRef.current = false;
    // Salida explícita del turno interrumpido (el tacho del panel): descartar
    // es lo único honesto que se puede hacer con audio que no viajó.
    setInterrupted(false);
    interruptedRef.current = false;
    setElapsed(0);
    showNoSound(false);
    const s = sessionRef.current;
    s.discardTurn();
    s.pauseCapture();
    stopSampling();
    samplesRef.current = [];
    discardTake();
  }, [clearTimer, stopSampling, discardTake, showNoSound]);

  const getEnergy = useCallback((): number => sessionRef.current.getMicEnergy(), []);
  const getTakeSamples = useCallback((): readonly number[] => samplesRef.current, []);

  // `commitPending` se prende al soltar (incluye el post-roll).
  const sending = session.commitPending;
  return useMemo<HoldRecorder>(
    () => ({
      recording,
      locked,
      paused,
      interrupted,
      sending,
      postRolling,
      sendLost,
      noSound,
      takeUrl,
      getTakeSamples,
      elapsed,
      getEnergy,
      start,
      lock,
      pause,
      resume,
      send,
      cancel,
    }),
    [
      recording,
      locked,
      paused,
      interrupted,
      sending,
      postRolling,
      sendLost,
      noSound,
      takeUrl,
      getTakeSamples,
      elapsed,
      getEnergy,
      start,
      lock,
      pause,
      resume,
      send,
      cancel,
    ],
  );
}
